import {
  Directive,
  OnInit,
  OnDestroy,
  OnChanges,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { GLOBAL_CONFIRMATION_DELETE } from '../constants/global-key.constans';

export interface EditableItem {
  uid?: string;
  description: string;
  ruleFK: string;
}

export interface FieldDef {
  defaultValue: any;
  validators?: any[];
  trackOriginal?: boolean;
  equals?: (a: any, b: any, group: FormGroup) => boolean;
}

export interface EditListSchema<TItem extends EditableItem> {
  arrayName: string;
  fields?: Record<string, FieldDef>; 
  sort?: (a: TItem, b: TItem) => number;
  confirmDeleteMessage?: string;
  confirmDeleteHeader?: string;
}
@Directive()
export abstract class EditListModalBase<TItem extends EditableItem>
  implements OnInit, OnDestroy, OnChanges
{
  readonly CONFIRM_DELETE = GLOBAL_CONFIRMATION_DELETE;
  
  @Input() items: TItem[] = [];
  @Input() ruleUid = '';
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  protected schema: Required<Omit<EditListSchema<TItem>, 'fields'>> & {
    fields: Record<string, FieldDef>;
  };

  form: FormGroup;
  protected destroy$ = new Subject<void>();

  constructor(
    protected fb: FormBuilder,
    schema: EditListSchema<TItem>,
    private confirmationService: ConfirmationService
  ) {
    this.schema = {
      arrayName: schema.arrayName,
      sort:
        schema.sort ?? ((a, b) => a.description.localeCompare(b.description)),
      confirmDeleteMessage:
        schema.confirmDeleteMessage ??
        'Êtes-vous sûr de vouloir supprimer cet élément ?',
      confirmDeleteHeader:
        schema.confirmDeleteHeader ?? 'Confirmation de suppression',
      fields: schema.fields ?? {}, 
    };

    this.form = this.fb.group({
      [this.schema.arrayName]: this.fb.array([]),
    });
  }

  get formArray(): FormArray<FormGroup> {
    return this.form.get(this.schema.arrayName) as FormArray<FormGroup>;
  }

  ngOnInit(): void {
    this.initializeForm(this.items);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']?.currentValue) {
      this.initializeForm(changes['items'].currentValue);
    }
    if (changes['visible']?.currentValue && this.items) {
      this.initializeForm(this.items);
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected initializeForm(items: TItem[]): void {
    const array = this.fb.array<FormGroup>([]);
    if (!items || items.length === 0) {
      const empty = this.createItemGroup(this.buildNewItem());
      empty.get('isNew')?.setValue(true);
      empty.get('originalMap')?.setValue(this.snapshotOriginal(empty));
      array.push(empty);
    } else {
      const sorted = [...items].sort(this.schema.sort);
      sorted.forEach((i) => array.push(this.createItemGroup(i)));
    }
    this.form.setControl(this.schema.arrayName, array);
  }

  protected buildNewItem(): TItem {
    const base: any = { uid: '', description: '', ruleFK: this.ruleUid };
    Object.entries(this.schema.fields).forEach(([k, def]) => {
      if (!(k in base)) base[k] = def.defaultValue;
    });
    return base as TItem;
  }

  protected createItemGroup(item: TItem): FormGroup {
    const descFromSchema = this.schema.fields['description'] ?? {};
    const descriptionDef: FieldDef = {
      validators: [Validators.required, Validators.minLength(1)],
      trackOriginal: true,
      ...descFromSchema,
    };
    const { description: _ignore, ...restFields } = this.schema.fields;
    const mergedFields: Record<string, FieldDef> = {
      description: descriptionDef,
      ...restFields, 
    };

    const controls: { [k: string]: FormControl } = {
      uid: new FormControl(item.uid),
      ruleFK: new FormControl(item.ruleFK),
      isNew: new FormControl(false),
      isDeleted: new FormControl(false),
      isModified: new FormControl(false),
      originalMap: new FormControl({} as Record<string, any>),
    };

    Object.entries(mergedFields).forEach(([key, def]) => {
      const val = (item as any)[key] ?? def.defaultValue;
      controls[key] = new FormControl(val, def.validators ?? []);
    });
    const group = new FormGroup(controls);

    group
      .get('originalMap')!
      .setValue(this.snapshotOriginal(group, mergedFields), {
        emitEvent: false,
      });

    Object.keys(mergedFields).forEach((key) => {
      group
        .get(key)!
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => this.recomputeIsModified(group, mergedFields));
    });

    return group;
  }

  protected snapshotOriginal(
    g: FormGroup,
    fields?: Record<string, FieldDef>
  ): Record<string, any> {
    const f = fields ?? {
      description: { defaultValue: '', trackOriginal: true },
    };
    const originals: Record<string, any> = {};
    const keys = new Set<string>([
      'description',
      ...Object.entries(f)
        .filter(([, d]) => d.trackOriginal)
        .map(([k]) => k),
    ]);
    keys.forEach((k) => (originals[k] = g.get(k)?.value));
    return originals;
  }

  protected recomputeIsModified(
    g: FormGroup,
    fields: Record<string, FieldDef>
  ): void {
    if (g.get('isNew')?.value) {
      g.get('isModified')?.setValue(false, { emitEvent: false });
      return;
    }
    const originals = g.get('originalMap')?.value as Record<string, any>;
    const keysToCheck = new Set<string>([
      'description',
      ...Object.entries(fields)
        .filter(([, d]) => d.trackOriginal)
        .map(([k]) => k),
    ]);
    const modified = Array.from(keysToCheck).some((key) => {
      const def = fields[key];
      const equals = def?.equals ?? ((a: any, b: any) => a === b);
      return !equals(g.get(key)?.value, originals[key], g);
    });
    g.get('isModified')?.setValue(modified, { emitEvent: false });
  }

  addNew(): void {
    const g = this.createItemGroup(this.buildNewItem());
    g.get('isNew')?.setValue(true);
    g.get('originalMap')?.setValue(this.snapshotOriginal(g), {
      emitEvent: false,
    });
    this.formArray.push(g);
  }

  deleteAt(index: number): void {
    const g = this.formArray.at(index);
    if (g.get('isNew')?.value) this.formArray.removeAt(index);
    else this.confirmDelete(() => {
      return g.get('isDeleted')?.setValue(true)
    });
  }

  isVisibleRow(i: number): boolean {
    return !this.formArray.at(i).get('isDeleted')?.value;
  }

  get isFormValid(): boolean {
    const active = this.formArray.controls.filter(
      (c) => !c.get('isDeleted')?.value
    );
    return active.length > 0 && active.every((c) => c.valid);
  }

  get validationMessage(): string {
    const active = this.formArray.controls.filter(
      (c) => !c.get('isDeleted')?.value
    );
    if (active.length === 0) return 'Au moins un élément est requis.';
    if (active.some((c) => c.invalid))
      return 'Veuillez corriger les erreurs dans le formulaire.';
    return '';
  }

  protected buildSubmitResult() {
    
    const data = this.form.value[this.schema.arrayName] as any[];
    return {
      toCreate: data.filter((x) => x.isNew && !x.isDeleted),
      toUpdate: data.filter((x) => !x.isNew && !x.isDeleted && x.isModified),
      toDelete: data.filter((x) => !x.isNew && x.isDeleted).map((x) => x.uid),
    };
  }
  
  protected confirmDelete(accept: () => void): void {
    this.confirmationService.confirm({
      key: this.CONFIRM_DELETE,
      message: this.schema.confirmDeleteMessage!,
      header: this.schema.confirmDeleteHeader!,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Non',
      accept,
    });
  }

  closeModal(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
